import { apiClient } from "services/api-client";

// vo-api-vendor reports a failed create/update/delete as HTTP 200 + { success: false, error } —
// turn that into a rejected promise so the UI never shows a success toast for it.
const requireSuccess = (response) => {
  if (response?.data?.success === false) {
    const error = new Error(response.data.error || 'Request rejected');
    error.code = response.data.code;
    error.response = response;
    throw error;
  }
  return response;
};

/**
 * Vendor Assessment API Service
 * Manages vendor records, risk classification, and assessment requests
 */

// ========== VENDOR CRUD OPERATIONS ==========

/**
 * Get all vendors for a company with optional pagination
 */
export const getVendors = async (tenant, page, perPage, filters = {}) => {
  let query = `/vendor/vendors?companyKey=${tenant}`;

  // The backend's page-size parameter is `limit` (it never read `perPage`, so every list silently
  // stopped at its default of 20). Callers without paging (vendor list, assessment wizard) sort and
  // filter client-side over the whole list, so ask for the backend maximum (100).
  query += page && perPage ? `&page=${page}&limit=${perPage}` : '&limit=100';

  // Add filters (tier, active status, search)
  if (filters.tier) {
    query += `&tier=${filters.tier}`;
  }
  if (filters.active !== undefined) {
    query += `&active=${filters.active}`;
  }
  if (filters.search) {
    query += `&search=${encodeURIComponent(filters.search)}`;
  }

  return await apiClient.get(query);
};

/**
 * Get a single vendor by ID
 */
export const getVendorById = async (id) => {
  return await apiClient.get(`/vendor/vendors/${id}`);
};

/**
 * Create a new vendor
 */
export const createVendor = async (vendor) => {
  return requireSuccess(await apiClient.post('/vendor/vendors', vendor));
};

/**
 * Update an existing vendor
 */
export const updateVendor = async (id, vendor) => {
  return requireSuccess(await apiClient.put(`/vendor/vendors/${id}`, vendor));
};

/**
 * Delete a vendor (soft delete)
 */
export const deleteVendor = async (id) => {
  return requireSuccess(await apiClient.delete(`/vendor/vendors/${id}`));
};

// ========== VENDOR ASSESSMENT OPERATIONS ==========

/**
 * Get assessment history for a vendor
 */
export const getVendorAssessments = async (vendorId) => {
  return await apiClient.get(`/vendor/assessment/vendor/${vendorId}/history`);
};

/**
 * Get vendor summary statistics
 */
export const getVendorStats = async (tenant) => {
  return await apiClient.get(`/vendor/vendors/stats?companyKey=${tenant}`);
};

// ========== RECERTIFICATION SETTINGS ==========

/**
 * Get tier-level recertification period defaults for the company
 */
export const getRecertificationSettings = async (tenant) => {
  return await apiClient.get(`/vendor/settings/recertification?companyKey=${tenant}`);
};

/**
 * Update tier-level recertification period defaults for the company
 * @param {Object} settings - { tier1?: number, tier2?: number, tier3?: number } (months)
 */
export const updateRecertificationSettings = async (settings) => {
  return await apiClient.put('/vendor/settings/recertification', settings);
};
